/**
 * Notification Preferences Modal
 * Allows users to customize which notifications they receive
 */
import React, { useState, useEffect } from 'react';
import { 
  Bell, Volume2, VolumeX, Mail, MessageSquare, Car, Calendar, 
  UserPlus, ClipboardList, DollarSign, AlertTriangle, X, Check,
  Smartphone, Monitor
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

// Default notification preferences
const DEFAULT_PREFERENCES = {
  // Sound notifications
  soundEnabled: true,
  soundNewBooking: true,
  soundNewApplication: true,
  soundNewRide: true,
  soundPayment: true,
  soundUrgent: true,
  
  // Browser push notifications
  browserEnabled: true,
  browserNewBooking: true,
  browserNewApplication: true,
  browserNewRide: true,
  browserPayment: true,
  browserUrgent: true,
  
  // In-app toast notifications
  toastEnabled: true,
  toastNewBooking: true,
  toastNewApplication: true,
  toastNewRide: true,
  toastPayment: true,
  toastUrgent: true,
  
  // Email notifications (stored on backend)
  emailNewBooking: true,
  emailNewApplication: true,
  emailRideUpdates: true,
  emailPaymentReceived: true,
  emailDailyDigest: false,
  emailWeeklyReport: true
};

// Storage key
const PREFS_KEY = 'nc_help_hub_notification_prefs';

// Load preferences from localStorage
export const loadNotificationPreferences = () => {
  try {
    const stored = localStorage.getItem(PREFS_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load notification preferences:', e);
  }
  return DEFAULT_PREFERENCES;
};

// Save preferences to localStorage
export const saveNotificationPreferences = (prefs) => {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    return true;
  } catch (e) {
    console.warn('Failed to save notification preferences:', e);
    return false;
  }
};

// Check if a specific notification type is enabled
export const isNotificationEnabled = (type, channel = 'sound') => {
  const prefs = loadNotificationPreferences();
  const masterKey = `${channel}Enabled`;
  const specificKey = `${channel}${type.charAt(0).toUpperCase() + type.slice(1)}`;
  
  return prefs[masterKey] && prefs[specificKey];
};

const NotificationPreferences = ({ isOpen, onClose }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setPrefs(loadNotificationPreferences());
      setHasChanges(false);
    }
  }, [isOpen]);
  
  const updatePref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };
  
  const handleSave = () => {
    saveNotificationPreferences(prefs);
    toast.success('Notification preferences saved!');
    setHasChanges(false);
    onClose();
  };
  
  const handleReset = () => {
    setPrefs(DEFAULT_PREFERENCES);
    setHasChanges(true);
  };
  
  if (!isOpen) return null;
  
  const NotificationRow = ({ icon: Icon, label, soundKey, browserKey, toastKey, color = 'text-[var(--text-primary)]' }) => (
    <div className="flex items-center justify-between py-3 border-b border-[var(--border-primary)] last:border-0">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-4">
        {soundKey && (
          <button
            onClick={() => updatePref(soundKey, !prefs[soundKey])}
            className={`p-1.5 rounded-lg transition-colors ${prefs[soundKey] && prefs.soundEnabled ? 'bg-blue-500/20 text-blue-400' : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'}`}
            title="Sound notification"
          >
            {prefs[soundKey] && prefs.soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        )}
        {browserKey && (
          <button
            onClick={() => updatePref(browserKey, !prefs[browserKey])}
            className={`p-1.5 rounded-lg transition-colors ${prefs[browserKey] && prefs.browserEnabled ? 'bg-purple-500/20 text-purple-400' : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'}`}
            title="Browser notification"
          >
            <Monitor className="w-4 h-4" />
          </button>
        )}
        {toastKey && (
          <button
            onClick={() => updatePref(toastKey, !prefs[toastKey])}
            className={`p-1.5 rounded-lg transition-colors ${prefs[toastKey] && prefs.toastEnabled ? 'bg-green-500/20 text-green-400' : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'}`}
            title="Toast notification"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--primary)]/20 rounded-xl">
              <Bell className="w-6 h-6 text-[var(--primary)]" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Notification Preferences</h2>
              <p className="text-sm text-[var(--text-muted)]">Customize your alerts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--background-subtle)] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Master Toggles */}
        <div className="bg-[var(--background-subtle)] rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Notification Channels
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Sound Alerts</span>
              </div>
              <Switch 
                checked={prefs.soundEnabled} 
                onCheckedChange={(v) => updatePref('soundEnabled', v)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-purple-400" />
                <span className="text-sm">Browser Notifications</span>
              </div>
              <Switch 
                checked={prefs.browserEnabled} 
                onCheckedChange={(v) => updatePref('browserEnabled', v)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-green-400" />
                <span className="text-sm">In-App Toasts</span>
              </div>
              <Switch 
                checked={prefs.toastEnabled} 
                onCheckedChange={(v) => updatePref('toastEnabled', v)}
              />
            </div>
          </div>
        </div>
        
        {/* Notification Types */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Notification Types
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Click icons to toggle: 
            <span className="text-blue-400 ml-2">🔊 Sound</span>
            <span className="text-purple-400 ml-2">🖥️ Browser</span>
            <span className="text-green-400 ml-2">💬 Toast</span>
          </p>
          
          <div className="border border-[var(--border-primary)] rounded-xl overflow-hidden">
            <NotificationRow 
              icon={Calendar} 
              label="New Bookings" 
              soundKey="soundNewBooking"
              browserKey="browserNewBooking"
              toastKey="toastNewBooking"
              color="text-blue-400"
            />
            <NotificationRow 
              icon={UserPlus} 
              label="New Applications" 
              soundKey="soundNewApplication"
              browserKey="browserNewApplication"
              toastKey="toastNewApplication"
              color="text-purple-400"
            />
            <NotificationRow 
              icon={Car} 
              label="New Ride Requests" 
              soundKey="soundNewRide"
              browserKey="browserNewRide"
              toastKey="toastNewRide"
              color="text-cyan-400"
            />
            <NotificationRow 
              icon={DollarSign} 
              label="Payments Received" 
              soundKey="soundPayment"
              browserKey="browserPayment"
              toastKey="toastPayment"
              color="text-green-400"
            />
            <NotificationRow 
              icon={AlertTriangle} 
              label="Urgent Alerts" 
              soundKey="soundUrgent"
              browserKey="browserUrgent"
              toastKey="toastUrgent"
              color="text-amber-400"
            />
          </div>
        </div>
        
        {/* Email Preferences */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Email Notifications
          </h3>
          <div className="space-y-3">
            {[
              { key: 'emailNewBooking', label: 'New booking confirmations' },
              { key: 'emailNewApplication', label: 'New worker applications' },
              { key: 'emailRideUpdates', label: 'Ride status updates' },
              { key: 'emailPaymentReceived', label: 'Payment received' },
              { key: 'emailDailyDigest', label: 'Daily activity digest' },
              { key: 'emailWeeklyReport', label: 'Weekly summary report' }
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between">
                <span className="text-sm">{item.label}</span>
                <Switch 
                  checked={prefs[item.key]} 
                  onCheckedChange={(v) => updatePref(item.key, v)}
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-[var(--border-primary)]">
          <Button variant="outline" onClick={handleReset} className="text-sm">
            Reset to Defaults
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            className="btn-primary"
            disabled={!hasChanges}
          >
            <Check className="w-4 h-4 mr-2" />
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
